import { useState, useMemo, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useUser } from "@/context/UserContext";
import type { KnowledgeArticle } from "@/types/knowledgeBase";
import { knowledgeCategories, convertFromDb } from "@/types/knowledgeBase";
import ArticleList from "@/pages/knowledge-base/components/ArticleList";
import ArticleDetail from "@/pages/knowledge-base/components/ArticleDetail";
import ArticleFormModal from "@/pages/knowledge-base/components/ArticleFormModal";

export default function KnowledgeBasePage() {
  const { isAdmin } = useUser();
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeSubCategory, setActiveSubCategory] = useState("全部");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Edit modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KnowledgeArticle | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Fetch articles from Supabase
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from("knowledge_articles")
        .select("*")
        .order("article_code", { ascending: true });

      if (queryError) {
        setError(queryError.message);
      } else if (data) {
        const converted = data.map((row: import("@/types/knowledgeBase").DbKnowledgeArticle) => convertFromDb(row));
        setArticles(converted);
        // Auto-select first article if none selected
        if (!selectedId && converted.length > 0) {
          setSelectedId(converted[0].id);
        }
      }
    } catch {
      setError("載入知識庫資料失敗，請稍後再試");
    }
    setLoading(false);
  }, [selectedId]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const filteredArticles = useMemo(() => {
    let result = [...articles];

    if (activeCategory !== "all") {
      result = result.filter((a) => a.category === activeCategory);
    }

    if (activeSubCategory !== "全部") {
      result = result.filter((a) => a.subCategory === activeSubCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          a.article_code.toLowerCase().includes(q) ||
          a.subCategory.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q)) ||
          a.suggestedReply.toLowerCase().includes(q)
      );
    }

    return result;
  }, [articles, searchQuery, activeCategory, activeSubCategory]);

  const selectedArticle = useMemo(() => {
    if (!selectedId) return null;
    return articles.find((a) => a.id === selectedId) || null;
  }, [articles, selectedId]);

  const handleSelectCategory = useCallback((id: string) => {
    setActiveCategory(id);
    setActiveSubCategory("全部");
  }, []);

  const handleSelectSubCategory = useCallback((sub: string) => {
    setActiveSubCategory(sub);
  }, []);

  const handleSelectArticle = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const handleCopyReply = useCallback((text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setToast("話術已複製到剪貼簿");
        setTimeout(() => setToast(null), 2000);
      })
      .catch(() => {
        setToast("複製失敗，請手動複製");
        setTimeout(() => setToast(null), 2000);
      });
  }, []);

  // Admin: Open create modal
  const handleCreate = useCallback(() => {
    setEditingArticle(null);
    setIsFormOpen(true);
  }, []);

  // Admin: Open edit modal
  const handleEdit = useCallback(() => {
    if (!selectedArticle) return;
    setEditingArticle(selectedArticle);
    setIsFormOpen(true);
  }, [selectedArticle]);

  // Admin: Delete article
  const handleDelete = useCallback(async () => {
    if (!selectedId || !isAdmin) return;
    if (!confirm("確定要刪除這篇文章嗎？此操作無法復原。")) return;

    setLoading(true);
    try {
      const { error: deleteError } = await supabase
        .from("knowledge_articles")
        .delete()
        .eq("id", selectedId);

      if (deleteError) {
        setToast(deleteError.message);
      } else {
        setToast("文章已刪除");
        setSelectedId(null);
        await fetchArticles();
      }
    } catch {
      setToast("刪除失敗，請稍後再試");
    }
    setLoading(false);
    setTimeout(() => setToast(null), 3000);
  }, [selectedId, isAdmin, fetchArticles]);

  // Admin: Submit create/update
  const handleFormSubmit = useCallback(
    async (data: Parameters<Parameters<typeof ArticleFormModal>[0]["onSubmit"]>[0]) => {
      setFormLoading(true);
      try {
        const dbData = {
          article_code: data.article_code,
          category: data.category,
          sub_category: data.subCategory,
          tags: data.tags,
          title: data.title,
          description: data.description,
          suggested_reply: data.suggestedReply,
          sop: data.sop,
          departments: data.departments,
          related_ids: data.relatedIds,
        };

        if (data.id) {
          // Update
          const { error: updateError } = await supabase
            .from("knowledge_articles")
            .update(dbData)
            .eq("id", data.id);

          if (updateError) {
            setToast(updateError.message);
          } else {
            setToast("文章已更新");
            setIsFormOpen(false);
            await fetchArticles();
          }
        } else {
          // Create
          const { error: insertError } = await supabase
            .from("knowledge_articles")
            .insert(dbData);

          if (insertError) {
            setToast(insertError.message);
          } else {
            setToast("文章已新增");
            setIsFormOpen(false);
            await fetchArticles();
          }
        }
      } catch {
        setToast("操作失敗，請稍後再試");
      }
      setFormLoading(false);
      setTimeout(() => setToast(null), 3000);
    },
    [fetchArticles]
  );

  // Stats
  const totalCount = articles.length;
  const productCount = articles.filter((a) => a.category === "product").length;
  const logisticsCount = articles.filter((a) => a.category === "logistics").length;
  const employeeCount = articles.filter((a) => a.category === "employee").length;
  const wineCount = articles.filter((a) => a.category === "wine").length;

  if (loading && articles.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-foreground-400">
        <div className="flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin text-xl" />
          <span className="text-sm">載入知識庫中...</span>
        </div>
      </div>
    );
  }

  if (error && articles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-foreground-400 gap-3">
        <i className="ri-error-warning-line text-3xl text-accent-500" />
        <p className="text-sm">{error}</p>
        <button
          onClick={fetchArticles}
          className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer"
        >
          重新載入
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-w-[1440px] mx-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-foreground-800 text-background-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <i className="ri-check-line text-primary-400" />
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="shrink-0 px-1 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-xl font-bold text-foreground-900"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              知識中心
            </h1>
            <p className="text-xs text-foreground-500 mt-0.5">
              共 <strong className="text-foreground-700">{totalCount}</strong> 則知識，商品
              <strong className="text-foreground-700"> {productCount}</strong> 則，物流
              <strong className="text-foreground-700"> {logisticsCount}</strong> 則，人事
              <strong className="text-foreground-700"> {employeeCount}</strong> 則，酒類
              <strong className="text-foreground-700"> {wineCount}</strong> 則
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line" />
                新增文章
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Two columns */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-4">
        {/* Left: Article List */}
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden flex flex-col min-h-[500px]">
          <ArticleList
            articles={filteredArticles}
            selectedId={selectedId}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            activeCategory={activeCategory}
            activeSubCategory={activeSubCategory}
            onSelectCategory={handleSelectCategory}
            onSelectSubCategory={handleSelectSubCategory}
            onSelectArticle={handleSelectArticle}
          />
        </div>

        {/* Right: Article Detail */}
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden flex flex-col min-h-[500px]">
          {selectedArticle ? (
            <ArticleDetail
              article={selectedArticle}
              allArticles={articles}
              onCopyReply={handleCopyReply}
              isAdmin={isAdmin}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-foreground-400 p-8">
              <i className="ri-book-open-line text-4xl mb-3 text-foreground-300" />
              <p className="text-sm">請選擇一篇文章查看詳情</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      <ArticleFormModal
        isOpen={isFormOpen}
        article={editingArticle}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        isLoading={formLoading}
      />
    </div>
  );
}